// LG vendor edit (R5, a11y — ADR-0010, minimum-viable; horizon-mechanics §3.22 + §4.29):
// APG "combobox with listbox popup" conformance. Removed the Tab hijack (Tab now does
// native focus movement — WCAG 2.1.1/2.1.2), added aria-expanded / aria-activedescendant
// toggling on the combobox input, and made a single Escape predictable (close results
// popup → else close dialog). Arrow Up/Down remain the result-navigation keys.
import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
// LG (search UI redesign Phase D, §3.28): the overlay is no longer a native <dialog>. It's a
// non-modal <search-panel-component> (lg-search-panel.js) that dispatches the SAME
// `dialog:open` / `dialog:close` events, so we still import those event names. DialogComponent
// (the shared modal controller) is no longer this component's host — dropped from the import.
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} [statusRegion] - The persistent polite live region for the result count (§4.65), outside the morph target.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product tiles (Phase C: emitted on the §4.52 history fetch, overlaid into the popular row's slot).
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the host panel controller (Phase D §3.28: the non-modal <search-panel-component>,
   * which exposes DialogComponent's showDialog/closeDialog/toggleDialog + dialog:open/close).
   * @returns {(HTMLElement & { open: boolean; showDialog(): void; closeDialog(): void; toggleDialog(): void }) | null}
   */
  get dialog() {
    return /** @type {any} */ (this.closest('search-panel-component'));
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      // focusout, not blur: blur does not bubble, and the listener lives on the component.
      this.addEventListener('focusout', this.#onComboboxFocusOut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      // Panel geometry (measuring the header bottom into `--search-panel-top`) now lives in the
      // panel controller (lg-search-panel.js #positionPanel), which owns open/close (Phase D §3.28).

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    // The popup is no longer displayed, so the combobox is collapsed (R5-b).
    this.#setExpanded(false);
    // Clear here too, not only on focusout: a close that leaves focus in the input would
    // otherwise keep the stale state until #resetSearch's async morph scrubs it.
    this.#clearActiveOption();
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    // The popup IS displayed from the moment the panel opens — the idle "Popular right now"
    // rail is a populated listbox — so the combobox is expanded here, not when a query lands
    // (R5-b). Fires on EVERY open: the `once` on this listener was removed with R5 A', and the
    // empty-state load keeps its own `#emptyStateLoaded` guard, so nothing loads twice.
    this.#setExpanded(true);
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  /**
   * The arrow-key set. R5 A' (2026-10-18): `.predictive-search-results__list` was dropped from
   * this selector. That class is carried by the queries list, the products list AND the resource
   * carousel — but the first two also carry `…__wrapper-queries` / `…__wrapper-products`, so
   * removing it subtracts the CAROUSEL and nothing else. The carousel is now outside the listbox
   * (its slides are `slideshow-component` chrome, which a listbox may not own), its links are
   * ordinary tab stops, and a vertical virtual cursor no longer steps sideways through a
   * mandatory-snap scroller.
   */
  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, .predictive-search-results__wrapper-products'
      )
    );

    // R5 Shape D: options are the LINKS, not their <li> wrappers, so collect by role rather than
    // by wrapper class. One selector for both rails; the <li>s are `role="presentation"`.
    //
    // `getClientRects().length` filters out options the browser does not render at all. The
    // popular rail emits a fixed five tiles and CSS hides the overflow at narrow widths, so at
    // 390x844 three of the five sit inside an `li.predictive-search-results__card` whose computed
    // `display` is `none` (the OPTION itself still computes `display: flex` at width 0 — testing
    // the option's own computed display would give the wrong answer) — measured on this branch
    // AND on QA/main,
    // so this is a long-standing gap, not a Shape D regression. Without the filter, arrow
    // presses 3, 4 and 5 moved `aria-activedescendant` onto nodes the accessibility tree
    // reports as `ignored: notRendered`: three of five presses announced nothing at all.
    //
    // This is a RENDERED test, not a visibility-to-AT test — `docs/accessibility.md` §5 is
    // explicit that the latter is an accessibility-tree question. `display: none` is precisely
    // the case where the two agree: no boxes, no AX node. Anything hidden by other means
    // (clipped, or scrolled out of the overflow container) still has boxes and is kept, because
    // the arrow model scrolls it into view. Two cases diverge the OTHER way and are worth
    // knowing: `visibility: hidden` and `aria-hidden` both keep their boxes while losing their
    // accessibility-tree node, so this filter would keep an inert option. Neither is used by any
    // rail today; if one ever is, this predicate stops being sufficient.
    const allItems = containers
      .flatMap((container) => Array.from(container.querySelectorAll('[role="option"]')))
      .filter((item) => item instanceof HTMLElement && item.getClientRects().length > 0);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Every option in the panel, UNFILTERED — the counterpart to `#allResultsItems`.
   *
   * Navigation uses the rendered set; CLEARING must not, and the distinction is load-bearing.
   * An option armed while rendered and then un-rendered — a rotation, a window resize, a zoom
   * change — drops out of the filtered set, so a clear written against that set can never reach
   * it again. Measured before this accessor existed: arm `product-3` at 1440, narrow to 390,
   * press ArrowDown, widen back → **two** options carrying `aria-selected="true"`, both
   * unignored in the accessibility tree. A single-select listbox reporting two selections is the
   * same 4.1.2 failure this ticket exists to close, re-entering by a different door.
   *
   * The rule, stated once: a getter narrowed for one consumer silently changes every other
   * consumer, and cleanup is where it bites — cleanup runs precisely when the narrowing
   * predicate has stopped holding.
   * @returns {HTMLElement[]}
   */
  get #allOptionNodes() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="option"]')));
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    if (!items?.length) return;

    // Clear across EVERY option, arm from the rendered set. Clearing over the rendered set alone
    // stranded the state of any option that stopped rendering between presses — see
    // `#allOptionNodes`.
    for (const item of this.#allOptionNodes) {
      item.removeAttribute('aria-selected');
      item.classList.remove('keyboard-focus');
    }

    const activeItem = items[index] ?? null;
    if (activeItem) {
      activeItem.setAttribute('aria-selected', 'true');
      if (this.#isKeyboardNavigation) {
        activeItem.classList.add('keyboard-focus');
      }
    }

    // APG combobox: focus stays on the input; the active option is pointed to via
    // aria-activedescendant. Every option carries a server-rendered id, gated in Liquid by the
    // same `in_listbox` flag as its role (L4, code review 2026-10-19: the client-side id/role
    // backfill was unreachable under Shape D and was removed).
    if (activeItem?.id) {
      this.refs.searchInput.setAttribute('aria-activedescendant', activeItem.id);
    } else {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }

    // The `inline: 'start'` carousel branch retired with R5 Shape D — no option lives in a
    // horizontal snap scroller any more, so `nearest` is correct for every option.
    activeItem?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
    this.refs.searchInput.focus();
  }

  /**
   * Drop the active-option state WITHOUT touching focus.
   *
   * Deliberately not `this.#currentIndex = -1`: that setter ends in `searchInput.focus()`, so
   * calling it from a focusout handler would yank focus back out of whatever the user just
   * tabbed to — a 2.4.3 defect introduced while fixing a 4.1.2 one.
   *
   * Why it is needed: `aria-activedescendant` is only honoured while the element carrying it
   * has DOM focus, so the stale attribute alone is inert. `aria-selected="true"` is NOT — it is
   * read in browse mode regardless of focus — and `.keyboard-focus` paints a filled ink chip.
   * Measured before this fix, on this branch and identically on QA/main: after one ArrowDown and
   * three Tabs, the first option still carried `aria-selected="true"` and `.keyboard-focus` while
   * the real focus ring sat on the Close button. Two indicators, pointing at different things —
   * the remaining half of this surface's R5-a finding, which otherwise closed with Shape D.
   */
  #clearActiveOption() {
    // Queries the DOM directly, NOT `#allResultsItems`. That getter is filtered to options the
    // browser renders, which is right for the arrow model and wrong for cleanup: when the panel
    // closes, lg-search-panel.js hides the content, every option loses its boxes, and the set is
    // empty — so a clear written against it silently does nothing on the one path that most
    // needs it. Measured before this correction: straight after Escape tier-2,
    // `optsRendered: 0` while `aria-selected` and `.keyboard-focus` both still sat on
    // `predictive-search-option-product-0`. The stale state only went away ~2.5s later, scrubbed
    // incidentally by #resetSearch's async morph — an abortable fetch, not a guarantee.
    for (const item of this.#allOptionNodes) {
      item.removeAttribute('aria-selected');
      item.classList.remove('keyboard-focus');
    }
    this.refs.searchInput.removeAttribute('aria-activedescendant');
  }

  /**
   * @param {FocusEvent} event
   */
  #onComboboxFocusOut = (event) => {
    if (event.target !== this.refs.searchInput) return;
    // Focus moving INTO an option would be a bug under Shape D (options are not tab stops), but
    // guard it anyway so a future change cannot silently clear the state it just set.
    const next = event.relatedTarget;
    if (next instanceof HTMLElement && next.closest('#predictive-search-listbox')) return;
    this.#clearActiveOption();
  };

  /**
   * Toggle the combobox popup expanded state on the input (APG combobox contract).
   *
   * R5-b (2026-10-18): this tracks whether the POPUP IS DISPLAYED, not whether a query returned
   * results. The old reading declared the combobox collapsed over an open 1325x399 popup holding
   * five options, while arrow keys drove a virtual cursor inside it — a screen reader was told
   * there was nothing to open while items were being made active.
   * @param {boolean} expanded - Whether the popup is displayed.
   */
  #setExpanded(expanded) {
    // B2 (code review 2026-10-19): `aria-controls`/`aria-owns` name the LISTBOX, and the listbox
    // is only rendered when option-bearing rails are. A query returning ONLY carousel resources
    // (measured: `q=return` → Pages carousel, 0 options, 0 listbox) would otherwise leave the
    // combobox asserting `aria-expanded="true"` against an IDREF that resolves to nothing — a
    // regression this boundary move introduced, and one axe reports as `incomplete`, not as a
    // violation, so only loading that state catches it.
    // L2 (code review 2026-10-19): gate on an OPTION existing, not on the wrapper — the Liquid no
    // longer prints an empty listbox, but an option is what the combobox actually expands onto.
    const hasOptions = Boolean(this.querySelector('#predictive-search-listbox [role="option"]'));
    const isExpanded = expanded && hasOptions;
    this.refs.searchInput.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    if (!isExpanded) this.refs.searchInput.removeAttribute('aria-activedescendant');
  }

  /**
   * Whether a real (queried) results popup is currently shown, as opposed to the
   * empty state / recently-viewed placeholder. The results section marks its
   * body with `[data-search-results]`; the empty state does not.
   * @returns {boolean}
   */
  get #hasResults() {
    return Boolean(this.refs.predictiveSearchResults.querySelector('[data-search-results]'));
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      // APG combobox: one Escape is predictable. If a queried results popup is
      // open, close only the popup (back to empty state) and keep the panel +
      // focus. Stop propagation so the panel controller's bubble-phase Escape
      // handler (lg-search-panel.js #onKeyDown) does NOT also close the panel on
      // the same press. If no results popup is open, let the event bubble to the
      // controller, which closes the panel (Phase D §3.28; two-tier Escape §4.29).
      if (this.#hasResults) {
        event.preventDefault();
        event.stopPropagation();
        this.#resetSearch();
      }
      return;
    }

    // Tab is intentionally NOT handled here: it must perform native focus
    // movement (reach reset / close / view-all / first result) — never result
    // navigation. Arrow Up/Down own result navigation (WCAG 2.1.1 / 2.1.2).
    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        // LG (a11y re-review 2026-09-28): this handler sits on the whole <form>, so without this
        // guard Enter on the Close / Clear buttons or a Tab-focused result link was hijacked
        // into "submit the query" (Close navigated to /search). Only the combobox input owns
        // Enter; every other control keeps its native activation (WCAG 2.1.1). Pre-existing.
        if (event.target !== this.refs.searchInput) return;

        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          // Shape D: every option IS an anchor, on both rails — activate it directly.
          this.#currentItem?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          // LG (search UI redesign Phase B, §3.27): pin type=product so the submitted-query
          // path lands on the products-only /search page (count honest). Only this
          // submit-the-query branch — the active-option / single-result paths keep their own
          // per-resource URLs.
          searchUrl.searchParams.set('type', 'product');
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();

        // Queried results popup is now shown: reflect it on the combobox (APG).
        this.#setExpanded(true);

        // WCAG 4.1.3 (§4.65): speak this settled result set from the persistent region. Reached only
        // on the non-aborted path, so a superseded fetch never writes — one write per settled set.
        this.#announce(predictiveSearchResults.querySelector('[data-status-message]')?.textContent ?? '');

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * The configured popular-rail source (`lg_popular_source`): `recently_viewed` (default),
   * `collection`, or `selected_products`. Set on the host in search-modal.liquid.
   * @returns {string}
   */
  get #popularSource() {
    return this.dataset.popularSource || 'recently_viewed';
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    searchInput.value = '';
    this.#hideResetButton();

    // Back to the empty / recently-viewed placeholder. Drop the active descendant now; the
    // expanded state is re-asserted at the END of this method, once the empty rail has been
    // morphed in and `#setExpanded`'s listbox gate can see it (W1).
    this.#currentIndex = -1;
    this.refs.searchInput.removeAttribute('aria-activedescendant');

    // Back to idle: the previous result-count message no longer describes what is shown. Clearing is
    // silent (removals are not announced under the default aria-relevant) and it re-arms the region,
    // so re-typing the same query after Escape-1 is announced again rather than deduplicated (§4.65).
    this.#announce('');

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated.
     *
     * LG (search UI redesign, Phase C — §4.52 single-row overlay): the pre-query state is
     * ONE configurable row (`lg_popular_source`). Only in `recently_viewed` mode does the
     * server row carry `[data-recently-viewed-slot]`; when the visitor has history we replace
     * that row's tiles IN PLACE (no separate "Recently viewed" + "Products" stack). In
     * `collection` / `selected_products` mode there is no slot, so the history fetch is
     * skipped and the server row stands alone. */
    const viewedProducts = RecentlyViewed.getProducts();
    const slot = parsedEmptySectionMarkup.querySelector('[data-recently-viewed-slot]');

    if (this.#popularSource === 'recently_viewed' && slot && viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (recentlyViewedMarkup) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        // The fetch renders a predictive-search-products-list; take its option tiles only.
        const historyTiles = parsedRecentlyViewedMarkup.querySelectorAll(
          '#predictive-search-products .predictive-search-results__wrapper-products > li'
        );
        if (historyTiles.length > 0) {
          slot.replaceChildren(...historyTiles);
          // The row now IS history — relabel it (the server label is "Popular right now"
          // unless the merchant set their own heading, in which case no data-recent-heading).
          const heading = slot.parentElement?.querySelector('[data-recent-heading]');
          if (heading instanceof HTMLElement && heading.dataset.recentHeading) {
            heading.textContent = heading.dataset.recentHeading;
          }
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();

    // W1 (a11y review 2026-10-19): re-assert expanded AFTER the morph, not before it.
    // `#setExpanded` gates on the listbox existing in the DOM, so it has to read the POST-morph
    // tree. Entering this method from a carousel-only query (`q=return` → Pages carousel, no
    // listbox, correctly collapsed) and pressing Escape-1 morphs the populated empty rail in —
    // and without this line the combobox stayed `aria-expanded="false"` over five live options
    // while arrows drove a virtual cursor inside them. That is verbatim R5-b, the defect this
    // ticket exists to fix, re-opened by the B2 fix made earlier the same day.
    // Measured: entering this state from `serum` passes by coincidence; the carousel-only
    // query is the one that exposes it.
    // L1 (code review 2026-10-19): …but only while the panel is actually OPEN. This method also
    // runs from `#handleDialogClose` (and from the idle empty-state preload while the panel is
    // closed), where an unconditional `true` left the combobox `aria-expanded="true"` over a
    // hidden popup. `dialog.open` is the panel controller's state — it flips to false at the START
    // of closeDialog, before the out-animation, so the close path always reads collapsed.
    this.#setExpanded(this.#isPanelOpen);
  };

  /**
   * Write the result-count message into the persistent polite live region (WCAG 4.1.3; §4.65).
   *
   * LG (fix/search-results-announcement): the region used to live INSIDE the morph target, so each
   * morph inserted a brand-new node already holding its final text — which no engine reliably
   * announces (Safari + VoiceOver: silent) — and the idle state had no region at all. The region now
   * sits in the component shell (snippets/search-modal.liquid) and exists before the first query;
   * only its TEXT changes. Identical text is skipped so a re-fetch of the same terms (e.g. a trailing
   * space) does not speak twice. Optional ref: a host without the region keeps today's behaviour.
   * @param {string} message - The localized message rendered by the section, or '' to clear.
   */
  #announce(message) {
    const region = this.refs.statusRegion;
    if (!region) return;
    const text = message.replace(/\s+/g, ' ').trim();
    if (region.textContent === text) return;
    region.textContent = text;
  }

  /**
   * Whether the host search panel is open (the popup can only be displayed while it is).
   * @returns {boolean}
   */
  get #isPanelOpen() {
    return Boolean(this.dialog?.open);
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
